import axios from 'axios'
import {Menu} from "@/models/Menu"
import {OrderDetails} from "@/models/OrderDetails"
import {Message} from "@/models/Message"
import {Character} from "@/models/Character"
import {Attendee} from "@/models/Attendee"
import {User} from "@/models/User"
import store from '../store'
import {v4 as uuidv4} from 'uuid'
import {ApiUtils} from "@/utils/ApiUtils"
import {SubOrder} from "@/models/SubOrder";
import {Category} from "@/models/Category"
import {CategoryLineItems} from '@/models/LineItem'

class Api {
  constructor(baseURL) {
    this.baseUrl = baseURL;
    this.initializeAxios();
  }


  get defaultHeaders() {
    return {
      'Accept': 'application/json'
    }
  }

  get managerUrl() {
    return `${this.baseUrl}/api/manager`
  }

  get orderUrl() {
    return `${this.managerUrl}/order`
  }

  get authUrl() {
    return `${this.baseUrl}/api/auth`
  }

  get attendeeUrl() {
    return `${this.baseUrl}/api/attendee`
  }

  get userUrl() {
    return `${this.baseUrl}/api/user`
  }

  get baseApiUrl() {
    return `${this.baseUrl}/api`
  }


  initializeAxios() {
    this.axios = axios.create({
      baseURL: this.baseUrl,
      headers: this.defaultHeaders
    });
    this.axios.interceptors.request.use(this.interceptRequest)
    this.axios.interceptors.response.use(null, this.interceptErrorResponse)
  }

  async interceptRequest(config) {
    // await store.dispatch('clearApiError');
    if (store.getters.isAuthenticated) {
      config.headers.Authorization = `Bearer ${store.getters.authToken}`
    }
    return config;
  }

  async interceptErrorResponse(error) {
    if (error.response?.status === 401) {
      await store.dispatch('signOut')
    } else {
      let errorMessage = ''
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response?.data?.errors)
        if (errors.length > 0 && errors[0].length > 0) {
          errorMessage = errors[0][0]
        }
      } else {
        errorMessage = error.response?.data?.message
      }
      await store.dispatch('resolveApiError', errorMessage ?? "Whoops. An error occurred")
    }
    throw error
  }

  // salesforce
  getSalesForceOauthUrl(data) {
    return this.axios.post(`${this.baseApiUrl}/salesforce/oauth-url`, data);
  }

  callSalesForceCallback(data) {
    return this.axios.post(`${this.baseApiUrl}/salesforce/callback`, data);
  }

  getSalesForceAccounts() {
    return this.axios.get(`${this.baseApiUrl}/salesforce/accounts`);
  }

  saveAttendeesToSF(data) {
    return this.axios.post(`${this.baseApiUrl}/salesforce/save-attendees`, data);
  }

  revokeSFtoken(orderid) {
    return this.axios.post(`${this.baseApiUrl}/salesforce/revoke-token/${orderid}`);
  }

  getSalesforceCustomObjects() {
    return this.axios.get(`${this.baseApiUrl}/salesforce/custom-objects`);
  }

  getCustomObjectFields(object) {
    return this.axios.get(`${this.baseApiUrl}/salesforce/custom-objects/${object}/fields`);
  }


  // Authentication
  async register(body) {
    const response = await this.axios.post(`${this.authUrl}/register`, body);
    const user = new User().map(response.data.data);
    return {token: response.data.token, user};
  }

  async registerUpdate(body) {
    const response = await this.axios.post(`${this.authUrl}/register-update`, body);
    const user = new User().map(response.data.data);
    return {token: response.data.token, user};
  }


  async login(body) {
    const response = await this.axios.post(`${this.authUrl}/login`, body);
    const user = new User().map(response.data.data);
    return {token: response.data.token, user, redirectTo: response.data.redirectTo};
  }

  async loginWithFacebook(body) {
    const response = await this.axios.post(`${this.authUrl}/login-social/facebook`, body);
    const user = new User().map(response.data.data);
    return {token: response.data.token, user};
  }

  async loginWithTwitter(body) {
    const response = await this.axios.post(`${this.baseUrl}/api/auth/login-social-twitter`, body);
    const user = new User().map(response.data.data);
    return {token: response.data.token, user};
  }

  async getLinkedinAccessToken(params) {
    const _params = new URLSearchParams(params);
    const {data} = await this.axios.post(`${this.baseUrl}/api/get-linkedin-token?${_params.toString()}`,
      null,
    );
    return data;
  }

  async initializeTwitterAuth(params) {
    const {data} = await this.axios.post(`${this.baseUrl}/api/get-twitter-token`, params);
    return data;
  }

  async getTwitterAccessToken(params) {
    const {data} = await this.axios.post(`${this.baseUrl}/api/get-twitter-access-token`, params,);
    return data;
  }

  async loginWithLinkedin(body) {
    const {data} = await this.axios.post(`${this.authUrl}/login-social/linkedin`, body);
    const user = new User().map(data.data);
    return {token: data.token, user};
  }

  async forgotPassword(body) {
    const response = await this.axios.post(`${this.authUrl}/password/forgot`, body);
    return response.data;
  }

  async resetPassword(body) {
    const response = await this.axios.post(`${this.authUrl}/password/reset`, body);
    return response.data;
  }

  verifyEmail(id, expires, hash) {
    return this.axios.get(`${this.authUrl}/email/verify/${id}/${hash}?expires=${expires}`);
  }

  resendVerifyEmail() {
    return this.axios.post(`${this.authUrl}/email/resend`)
  }

  async isVerified() {
    return this.axios.get(`${this.authUrl}/email/verified`)
  }

  async changePassword(body) {
    await this.axios.post(`${this.managerUrl}/change-password`, body)
  }

  async deleteAccount() {
    await this.axios.delete(`${this.managerUrl}/profile`)
  }

  // Profile
  async getPersonalDetails() {
    const response = await this.axios.get(`${this.userUrl}/info`);
    return new User().map(response.data.data);
  }

  async updatePersonalDetails(body) {
    const response = await this.axios.post(`${this.userUrl}/personal-details`, body);
    return new User().map(response.data);
  }

  // Menu
  async getMenu(catererId) {
    const response = await this.axios.get(`${this.managerUrl}/menu/caterer/${catererId}`);
    return new Menu().map(response.data);
  }

  // Order
  async checkLocationAvailability(body) {
    return await this.axios.post(`${this.managerUrl}/locations`, body);
  }

  checkLocation(location) {
    return this.axios.post(`${this.managerUrl}/locations`, {index: location})
  }

  async getOrder(orderID) {
    const response = await this.axios.get(`${this.orderUrl}/${orderID}`);
    return new OrderDetails().map(response.data);
  }

  async getOrderByUuid(orderUUID) {
    const response = await this.axios.get(`${this.orderUrl}/uuid/${orderUUID}`);
    return new OrderDetails().map(response.data);
  }

  async createOrder(body) {
    const response = await this.axios.post(this.orderUrl, body);
    return new OrderDetails().map(response.data);
  }

  async updateOrder(orderId, body) {
    const response = await this.axios.put(`${this.orderUrl}/${orderId}`, body);
    return new OrderDetails().map(response.data);
  }

  async finalizeOrder(orderId) {
    const response = await this.axios.get(`${this.orderUrl}/${orderId}/finalize`);
    return new OrderDetails().map(response.data);
  }

  async finalizeOrderWithCoupon(orderId, coupon) {
    const response = await this.axios.get(`${this.orderUrl}/${orderId}/finalize/?promo_code=${coupon}`);
    return new OrderDetails().map(response.data);
  }

  async payForOrder(orderId, body) {
    const response = await this.axios.post(`${this.orderUrl}/${orderId}/pay`, body);
    return response.data?.clientSecret;
  }

  // Order menu
  async createOrderMenu(orderId, body) {
    const response = await this.axios.post(`${this.orderUrl}/${orderId}/order-menu`, body);
    return new OrderDetails().map(response.data);
  }

  async updateOrderMenu(orderId, body) {
    const response = await this.axios.post(`${this.orderUrl}/${orderId}/order-menu-update`, body);
    return new OrderDetails().map(response.data);
  }

  //Order categories
  async getCategories(orderId) {
    const response = await this.axios.get(`${this.managerUrl}/order/${orderId}/order-categories`)
    return response.data.categories.map(category => new Category().map(category))
  }

  async createOrderCategories(orderId, body) {
    const response = await this.axios.post(`${this.managerUrl}/order/${orderId}/order-categories`, body);
    return response.data?.categories.map(category => new Category().map(category));
  }

  //Line Items
  async getLineItems(orderId) {
    const response = await this.axios.get(`${this.managerUrl}/order/${orderId}/order-items`)
    return response.data.categories.map(category => new CategoryLineItems().map(category))
  }

  async createLineItems(orderId, body) {
    const response = await this.axios.post(`${this.managerUrl}/order/${orderId}/order-items`, body);
    return response.data?.categories.map(category => new CategoryLineItems().map(category))
  }

  // Order characters
  async createOrderCharacters(orderId, body) {
    const response = await this.axios.post(`${this.orderUrl}/${orderId}/characters`, body);
    return response.data?.characters.map(character => new Character().map(character));
  }

  async getOrderDefaultCharacters() {
    try {
      const response = await this.axios.get(`${this.orderUrl}/default-characters`);
      return response.data?.characters.map(character => new Character().map(character));
    } catch (error) {
      throw error.response?.data;
    }
  }

  async getOrderCharacters(orderId) {
    const response = await this.axios.get(`${this.orderUrl}/${orderId}/characters`,);
    return response.data?.characters.map(character => new Character().map(character));
  }

  // Order Invite

  async inviteToOrder(orderId, body) {
    const response = await this.axios.post(`${this.attendeeUrl}/order/${orderId}/join`, body);
    return response;
  }

  // Order messages
  async createOrderMessage(orderId, body) {
    const {data} = await this.axios.post(`${this.orderUrl}/${orderId}/messages`, body);
    return new Message().map(data);
  }

  async createOrderMessages(orderId, body) {
    const {data} = await this.axios.post(`${this.orderUrl}/${orderId}/messages-create`, body);
    return data.messages?.map(msg => new Message(msg).map(data));
  }

  async updateOrderMessage(orderId, messageId, body) {
    const {data} = await this.axios.put(`${this.orderUrl}/${orderId}/messages/${messageId}`, body);
    return new Message().map(data);
  }

  async deleteOrderMessage(orderId, messageId) {
    await this.axios.delete(`${this.orderUrl}/${orderId}/messages/${messageId}`);
  }

  async getOrderMessages(orderId) {
    const {data: {messages}} = await this.axios.get(`${this.orderUrl}/${orderId}/messages`);
    return messages.map(message => new Message().map(message));
  }

  async getOrderMessagesTrigger() {
    const data = await this.axios.get(`${this.managerUrl}/events`);
    return data.data.events;
  }

  // Order Attendees
  async inviteAttendees(orderId, body) {
    try {
      await this.axios.post(`${this.orderUrl}/${orderId}/invite-attendees`, body);
    } catch (e) {
      throw ApiUtils.extractBackendErrors(e);
    }
  }

  async inviteAttendeesCSV(orderId, body) {
    const {data} = await this.axios.post(`${this.orderUrl}/${orderId}/invite-attendees-csv`, body);
    return data.attendees?.map(attendee => new Attendee().map(attendee)).map(attendee => {
      attendee.id = uuidv4()
      return attendee;
    })
  }

  async getOrderAttendees(orderId) {
    const response = await this.axios.get(`${this.orderUrl}/${orderId}/invited-attendees`);
    return response.data?.attendees.map(attendee => new Attendee().map(attendee));
  }

  async sendAttendeeInvitation(orderId) {
    await this.axios.get(`${this.orderUrl}/${orderId}/send-invitations`);
  }

  // Validations
  async validateOrderDetails(body) {
    await this.axios.post(`${this.managerUrl}/check/order`, body);
  }

  async validateOrderMenu(body) {
    await this.axios.post(`${this.managerUrl}/check/menu`, body);
  }

  async validateOrderAttendees(body) {
    await this.axios.post(`${this.managerUrl}/check/attendee`, body);
  }

  async loginAttendeeViaInvite(body) {
    try {
      const response = await axios.post(`${this.authUrl}/login-invite`, body);
      const user = new User().map(response.data.data);
      const order = new OrderDetails().map(response.data.data.order);
      return ({user, order, token: response.data.token});
    } catch (error) {
      throw error.response?.data;
    }
  }

  async getAttendeeOrderMenu(orderId) {
    try {
      const response = await this.axios.get(`${this.attendeeUrl}/order/menu/${orderId}`);
      return new OrderDetails().map(response.data);
    } catch (error) {
      throw error.response?.data;
    }
  }

  async getAttendeeOrderAvailableCharacters(orderId) {
    try {
      const response = await this.axios.get(`${this.attendeeUrl}/order/${orderId}/available-characters`);
      return response.data.characters.map(character => new Character().map(character));
    } catch (error) {
      throw error.response?.data;
    }
  }

  async getSubOrderCharacter(orderId) {
    const {data} = await this.axios.get(`${this.attendeeUrl}/order/${orderId}/characters`);
    return new Character().map(data);
  }

  async getAttendeeSubOrder(id) {
    try {
      const response = await this.axios.get(`${this.attendeeUrl}/order/${id}`);
      return new SubOrder().map(response.data)
    } catch (error) {
      throw error.response;
    }
  }

  async createAttendeeSubOrder(body) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order`, body);
      return new SubOrder().map(response.data);
    } catch (error) {
      throw error.response;
    }
  }

  async updateAttendeeSubOrder(body, id) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order-menu/${id}`, body);
      return new SubOrder().map(response.data);
    } catch (error) {
      console.log(error)
      throw error.response?.data;
    }
  }

  async createAttendeeSubOrderMenu(body) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order-menu/`, body);
      return new SubOrder().map(response.data);
    } catch (error) {
      console.log(error)
      throw error.response?.data;
    }
  }

  async selectAttendeeOrderCharacter(orderId, body) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order/${orderId}/characters`, body);
      return new Character().map(response.data);
    } catch (error) {
      throw error.response?.data;
    }
  }

  async updateAttendeeOrderCharacter(orderId, body) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order/${orderId}/characters-update`, body);
      return new Character().map(response.data);
    } catch (error) {
      throw error.response?.data;
    }
  }

  async finishAttendeeSubOrder(orderId) {
    try {
      const response = await this.axios.post(`${this.attendeeUrl}/order/${orderId}/complete`);
      return new Character().map(response.data);
    } catch (error) {
      throw error.response?.data;
    }
  }

  // Manager - my orders
  async getOrders(options) {
    return await this.axios.get(`${this.managerUrl}/orders`, options);
  }

  getOrderDetails(uuid) {
    return this.axios.get(`${this.managerUrl}/orders/${uuid}`)
  }

  getInvitedAttendeesForOrder(uuid) {
    return this.axios.get(`${this.managerUrl}/orders/${uuid}/attendees`)
  }

  copyOrderTemplate(uuid) {
    return this.axios.post(`${this.managerUrl}/orders/${uuid}/duplicate`)
  }

  updateOrderStatus(uuid, status) {
    return this.axios.patch(`${this.managerUrl}/orders/${uuid}/status`, {
      status: status
    })
  }

  getMessagesForOrder(uuid) {
    return this.axios.get(`${this.managerUrl}/orders/${uuid}/messages`)
  }

  // Events Related Endpoints
  getMyEvents() {
    return this.axios.get(`${this.baseApiUrl}/events`)
  }

  getEventDetails(uuid) {
    return this.axios.get(`${this.baseApiUrl}/events/${uuid}`)
  }

  // Manager - order details - collaborators
  async getOrderCollaborators(id) {
    return await this.axios.get(`${this.managerUrl}/orders/${id}/collaborators`);
  }

  // Manager - order - add collaborator
  async addProjectCollaborator(orderid, options) {
    return await this.axios.post(`${this.managerUrl}/orders/${orderid}/collaborators`, options);
  }

  // Segmentation Process Endpoints
  createOrderWith(data) {
    return this.axios.post(`${this.managerUrl}/segmentation/order`, data)
  }

  updateOrderWith(uuid, data) {
    return this.axios.patch(`${this.managerUrl}/segmentation/order/${uuid}`, data)
  }

  addCollaboratorToOrder(uuid, firstName, lastName, email, phone, company, role = 'collaborator') {
    return this.axios.post(`${this.managerUrl}/segmentation/order/${uuid}/staff`, {
      role: role,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      company: company
    })
  }

  sendInvitesToCollaborators(uuid) {
    return this.axios.post(`${this.managerUrl}/segmentation/order/${uuid}/staff/send-invites`)
  }

  addOwnerToOrder(uuid, user_id) {
    return this.axios.post(`${this.managerUrl}/segmentation/order/${uuid}/owner`, {
      user_id: user_id
    })
  }

  getSegmentationOrderDetails(uuid) {
    return this.axios.get(`${this.managerUrl}/segmentation/order/${uuid}`)
  }

  // Manager - order - remove collaborator
  async removeProjectCollaborator(orderid, userid) {
    return await this.axios.delete(`${this.managerUrl}/orders/${orderid}/collaborators/${userid}`);
  }

  removeCollaboratorFromOrder(uuid, userId, role) {
    return this.axios.delete(`${this.managerUrl}/orders/${uuid}/collaborators/${userId}`, {
      params: {
        role: role
      }
    })
  }

  printOrder(uuid) {
    return this.axios.get(`${this.managerUrl}/orders/${uuid}/print`);
  }
}

export default new Api(
  process.env.VUE_APP_API_URL,
);
